/**
 * TenantSEO.jsx — per-tenant SEO meta + structured data (JSON-LD).
 *
 * Emits:
 *   - <title>, meta description, canonical, OpenGraph, Twitter card
 *   - JSON-LD AutoRepair / LocalBusiness with address, geo, openingHours, ratings
 *   - JSON-LD Service (per offered service) and FAQPage
 *   - JSON-LD BreadcrumbList for sub-pages
 *
 * Goal: tenant pages outrank Motopotti's own landing for "<garage name>" and
 * "<service> <area> Dhaka"-style queries.
 */

import React from 'react';
import { Helmet } from 'react-helmet-async';

const DAY_MAP_SCHEMA = {
    mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday',
    fri: 'Friday', sat: 'Saturday', sun: 'Sunday'
};

function buildLocalBusinessLD(tenant, origin, slug) {
    const url = `${origin}/${slug}`;
    const opening = tenant?.opening_hours || {};
    const openingHoursSpecification = Object.entries(opening)
        .filter(([, h]) => h && !h.closed && h.open && h.close)
        .map(([day, h]) => ({
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: DAY_MAP_SCHEMA[day] || day,
            opens: h.open,
            closes: h.close
        }));

    const ld = {
        '@context': 'https://schema.org',
        '@type': 'AutoRepair',
        '@id': `${url}#workshop`,
        name: tenant?.name,
        url,
        image: tenant?.cover_image_url || tenant?.logo_url,
        logo: tenant?.logo_url,
        telephone: tenant?.phone,
        priceRange: '৳৳',
        description: tenant?.tagline || tenant?.description || tenant?.about || `${tenant?.name} — motorcycle workshop in Bangladesh.`,
        address: {
            '@type': 'PostalAddress',
            streetAddress: tenant?.address || tenant?.location,
            addressLocality: tenant?.city || 'Dhaka',
            addressRegion: tenant?.region || 'Dhaka',
            addressCountry: 'BD'
        },
        areaServed: tenant?.area_served || tenant?.city || 'Dhaka',
        sameAs: [
            tenant?.facebook_url,
            tenant?.instagram_url,
            tenant?.youtube_url,
            tenant?.website_url
        ].filter(Boolean)
    };

    if (tenant?.latitude && tenant?.longitude) {
        ld.geo = {
            '@type': 'GeoCoordinates',
            latitude: tenant.latitude,
            longitude: tenant.longitude
        };
    }
    if (openingHoursSpecification.length > 0) ld.openingHoursSpecification = openingHoursSpecification;

    if (tenant?.review_summary?.count > 0) {
        ld.aggregateRating = {
            '@type': 'AggregateRating',
            ratingValue: tenant.review_summary.average,
            reviewCount: tenant.review_summary.count
        };
    }
    return ld;
}

function buildServiceLDs(tenant, services, origin, slug) {
    const provider = { '@type': 'AutoRepair', name: tenant?.name, url: `${origin}/${slug}` };
    return (services || []).slice(0, 25).map(s => ({
        '@context': 'https://schema.org',
        '@type': 'Service',
        name: s.name,
        description: s.description,
        provider,
        areaServed: tenant?.city || 'Dhaka',
        ...(s.price > 0 ? {
            offers: { '@type': 'Offer', price: s.price, priceCurrency: 'BDT' }
        } : {})
    }));
}

function buildFAQLD(tenant) {
    const faq = tenant?.faq;
    if (!Array.isArray(faq) || faq.length === 0) return null;
    return {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faq.map(it => ({
            '@type': 'Question',
            name: it.q,
            acceptedAnswer: { '@type': 'Answer', text: it.a }
        }))
    };
}

function buildBreadcrumbLD(origin, slug, page) {
    const items = [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${origin}/${slug}` }
    ];
    if (page === 'services') items.push({ '@type': 'ListItem', position: 2, name: 'Services', item: `${origin}/${slug}/services` });
    if (page === 'store') items.push({ '@type': 'ListItem', position: 2, name: 'Parts Store', item: `${origin}/${slug}/store` });
    return {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: items
    };
}

export default function TenantSEO({ tenant, slug, page = 'home', services = [] }) {
    if (!tenant) return null;
    const origin = (typeof window !== 'undefined' && window.location?.origin) || 'https://motopotti.com';
    const baseTitle = tenant.name || 'Motopotti workshop';
    const cityBit = tenant.city ? ` — ${tenant.city}` : '';
    const titleByPage = {
        home: `${baseTitle}${cityBit} | Motorcycle workshop`,
        services: `Services at ${baseTitle}${cityBit} | Motorcycle repair menu`,
        store: `Parts store — ${baseTitle}${cityBit} | Genuine motorcycle parts`
    };
    const title = titleByPage[page] || titleByPage.home;
    const description = (tenant.tagline || tenant.description || tenant.about || `${baseTitle} — trusted motorcycle workshop. Genuine parts, master mechanics, transparent pricing.`).slice(0, 200);
    const canonical = `${origin}/${slug}${page === 'home' ? '' : '/' + page}`;
    const cover = tenant.cover_image_url || tenant.logo_url;

    const ldEntries = [buildLocalBusinessLD(tenant, origin, slug), buildBreadcrumbLD(origin, slug, page)];
    if (page === 'home') {
        const faq = buildFAQLD(tenant);
        if (faq) ldEntries.push(faq);
    }
    if (page === 'services') ldEntries.push(...buildServiceLDs(tenant, services, origin, slug));

    return (
        <Helmet>
            <title>{title}</title>
            <meta name="description" content={description} />
            <link rel="canonical" href={canonical} />
            <meta name="robots" content="index, follow, max-image-preview:large" />

            {/* OpenGraph */}
            <meta property="og:type" content="business.business" />
            <meta property="og:title" content={title} />
            <meta property="og:description" content={description} />
            <meta property="og:url" content={canonical} />
            <meta property="og:site_name" content={tenant.name} />
            {cover && <meta property="og:image" content={cover} />}

            {/* Twitter */}
            <meta name="twitter:card" content={cover ? 'summary_large_image' : 'summary'} />
            <meta name="twitter:title" content={title} />
            <meta name="twitter:description" content={description} />
            {cover && <meta name="twitter:image" content={cover} />}

            {/* Geo */}
            {tenant.latitude && tenant.longitude && (
                <>
                    <meta name="geo.position" content={`${tenant.latitude};${tenant.longitude}`} />
                    <meta name="ICBM" content={`${tenant.latitude}, ${tenant.longitude}`} />
                </>
            )}
            {tenant.city && <meta name="geo.placename" content={tenant.city} />}
            <meta name="geo.region" content="BD" />

            {/* Structured data */}
            {ldEntries.map((ld, i) => (
                <script key={i} type="application/ld+json">
                    {JSON.stringify(ld)}
                </script>
            ))}
        </Helmet>
    );
}
